import type {HistoryDomainAdapterRegistry} from '../domain';
import {
    createSessionModelHistoryManager,
    type SessionModelHistoryManager,
    type SessionModelHistoryManagerOptions,
    type SessionModelHistoryState,
} from '../model';
import type {
    CadTransaction,
    HistoryApplyDirection,
} from '../types';
import {applyTransactionPatches} from '../PatchApplier';

/**
 * 会话模型历史运行时配置。
 *
 * 设计说明：
 * 该运行时把“事务排队、序号分配、PatchApplier 调用、热/冷历史切换”统一收口在 cad-core。
 * web-client 只负责提供 UI 领域写入上下文，不能直接操作 ModelHistoryManager 内部栈。
 */
export interface QueuedSessionModelHistoryRuntimeOptions<TDomainContext = unknown>
    extends Omit<SessionModelHistoryManagerOptions, 'applyTransaction' | 'adapterRegistry'> {
    /** 历史领域 adapter 注册表。 */
    readonly adapterRegistry: HistoryDomainAdapterRegistry;
    /** 创建当前宿主领域上下文。 */
    readonly createDomainContext: () => TDomainContext;
    /** 应用事务前的宿主回调；通常用于推进 Worker epoch。 */
    readonly beforeApplyTransaction?: (transaction: CadTransaction, direction: HistoryApplyDirection) => void;
    /** 提交事务成功后的宿主回调。 */
    readonly afterCommitTransaction?: (transaction: CadTransaction) => void;
}

/**
 * 会话模型历史运行时。
 */
export interface QueuedSessionModelHistoryRuntime {
    /**
     * 分配下一条模型历史序号。
     *
     * @returns 单调递增事务序号。
     */
    readonly allocateSequence: () => number;
    /**
     * 提交模型事务。
     *
     * @param transaction 待提交事务。
     * @returns 实际入栈事务；空事务返回 null。
     */
    readonly commitTransaction: (transaction: CadTransaction) => Promise<CadTransaction | null>;
    /**
     * 撤销模型事务。
     *
     * @returns 被撤销事务；无事务时返回 null。
     */
    readonly undo: () => Promise<CadTransaction | null>;
    /**
     * 重做模型事务。
     *
     * @returns 被重做事务；无事务时返回 null。
     */
    readonly redo: () => Promise<CadTransaction | null>;
    /**
     * 清空会话历史。
     */
    readonly clear: () => Promise<void>;
    /**
     * 只清空内存历史并重置序号。
     */
    readonly clearMemory: () => void;
    /**
     * 读取模型历史状态。
     *
     * @returns 历史状态。
     */
    readonly getState: () => SessionModelHistoryState;
    /**
     * 在模型历史队列中串行执行宿主操作。
     *
     * @param operation 需要与 undo / redo / commit 互斥的操作。
     * @returns 操作结果。
     */
    readonly runExclusive: <TResult>(operation: () => Promise<TResult>) => Promise<TResult>;
}

/**
 * 创建排队执行函数。
 *
 * @returns 排队器。
 */
function createHistoryOperationQueue(): {
    readonly enqueue: <TResult>(operation: () => Promise<TResult>) => Promise<TResult>;
    readonly runOrEnqueue: <TResult>(operation: () => Promise<TResult>) => Promise<TResult>;
} {
    let queue: Promise<unknown> = Promise.resolve();
    let activeDepth = 0;

    const enqueue = <TResult>(operation: () => Promise<TResult>): Promise<TResult> => {
        const queuedOperation = queue.then(async () => {
            activeDepth += 1;
            try {
                return await operation();
            } finally {
                activeDepth = Math.max(0, activeDepth - 1);
            }
        });

        // 队列必须在失败后继续可用，具体错误仍由当前调用方接收。
        queue = queuedOperation.catch(() => undefined);

        return queuedOperation;
    };

    return {
        enqueue,
        runOrEnqueue(operation) {
            return activeDepth > 0 ? operation() : enqueue(operation);
        },
    };
}

/**
 * 创建会话模型历史运行时。
 *
 * @param options 运行时配置。
 * @returns 会话模型历史运行时。
 */
export function createQueuedSessionModelHistoryRuntime<TDomainContext = unknown>(
    options: QueuedSessionModelHistoryRuntimeOptions<TDomainContext>,
): QueuedSessionModelHistoryRuntime {
    let sequence = 0;
    const operationQueue = createHistoryOperationQueue();
    const manager: SessionModelHistoryManager = createSessionModelHistoryManager({
        ...options,
        applyTransaction(transaction, direction) {
            options.beforeApplyTransaction?.(transaction, direction);
            applyTransactionPatches(transaction, direction, {
                adapterRegistry: options.adapterRegistry,
                domainContext: options.createDomainContext(),
            });
        },
    });

    return {
        allocateSequence() {
            sequence += 1;

            return sequence;
        },
        commitTransaction(transaction) {
            return operationQueue.runOrEnqueue(async () => {
                const committedTransaction = await manager.commit(transaction);

                if (committedTransaction) {
                    options.afterCommitTransaction?.(committedTransaction);
                }

                return committedTransaction;
            });
        },
        undo() {
            return operationQueue.runOrEnqueue(() => manager.undo());
        },
        redo() {
            return operationQueue.runOrEnqueue(() => manager.redo());
        },
        clear() {
            return operationQueue.runOrEnqueue(async () => {
                await manager.clear();
                sequence = 0;
            });
        },
        clearMemory() {
            manager.clearMemory();
            sequence = 0;
        },
        getState() {
            return manager.getState();
        },
        runExclusive(operation) {
            return operationQueue.enqueue(operation);
        },
    };
}
